import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LegalDocument, CaseFile, CalendarEvent, UserData } from '../types';
import { analyzeDocumentWithDeadlines, generateEmbeddings, chunkText } from '../services/geminiService';
import { db } from '../services/databaseService';
import { isSupabaseConfigured } from '../services/supabaseClient';
import { RateLimitService } from '../services/rateLimitService';
import * as pdfjsLib from 'pdfjs-dist';
import { toast } from 'sonner';
import { Calculator, Wand2, Calendar as CalendarIcon } from 'lucide-react';
import DeadlineCalculatorModal from './DeadlineCalculatorModal';

// Configurare worker pentru pdf.js (Folosim versiunea stabilă 3.11.174 care există pe CDN)
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

interface DocumentsViewProps {
  documents: LegalDocument[];
  cases: CaseFile[];
  user: UserData;
  onAddDocument: (doc: LegalDocument) => void;
  onUpdateDocument: (id: string, updates: Partial<LegalDocument>) => void;
  onDeleteDocument: (id: string) => void;
  onAddEvent: (event: CalendarEvent) => void;
}

const DocumentsView: React.FC<DocumentsViewProps> = ({ documents, cases, user, onAddDocument, onUpdateDocument, onDeleteDocument, onAddEvent }) => {
  const navigate = useNavigate();
  const [isUploading, setIsUploading] = useState(false);
  const [selectedCaseId, setSelectedCaseId] = useState('');
  const [activeAnalysisId, setActiveAnalysisId] = useState<string | null>(null);
  const [localDocs, setLocalDocs] = useState<LegalDocument[]>([]);
  const [uploadStatus, setUploadStatus] = useState<string>('');
  const [openingId, setOpeningId] = useState<string | null>(null);
  const [isCalculatorOpen, setIsCalculatorOpen] = useState(false);

  useEffect(() => {
    const loadDocs = async () => {
      try {
        const data = await db.getDocuments();
        setLocalDocs(data);
      } catch (err) {
        setLocalDocs(documents);
      }
    };
    loadDocs();
  }, [documents]);

  const extractTextFromPDF = async (file: File): Promise<string> => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      let fullText = '';
      
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map((item: any) => item.str).join(' ');
        fullText += pageText + '\n';
      }
      return fullText;
    } catch (err) {
      console.error("Eroare la extragerea textului din PDF:", err);
      return "";
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // --- Rate Limiting Check ---
    if (!RateLimitService.checkDailyLimit('uploads')) {
      toast.error(`⚠️ Limita zilnică de încărcări atinsă.`, {
        description: "În perioada beta ai atins limita zilnică.",
        duration: 5000,
      });
      if (e.target) e.target.value = '';
      return;
    }

    if (!RateLimitService.checkTotalLimitSync('docs', localDocs.length)) {
      toast.error("Limită Documente", {
        description: "Ai atins limita de documente pentru versiunea Beta."
      });
      return;
    }

    const fileSizeMB = file.size / (1024 * 1024);
    let maxAllowedMB = 15; // flat beta limit

    if (fileSizeMB > maxAllowedMB) {
      toast.error("Fișier prea mare!", {
        description: `Limita în versiunea beta este de ${maxAllowedMB}MB pe fișier.`
      });
      if (e.target) e.target.value = '';
      return;
    }

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Tip de fișier neacceptat", { description: "Te rugăm să folosești PDF, DOCX sau Imagini." });
      if (e.target) e.target.value = '';
      return;
    }

    if (!isSupabaseConfigured) {
      toast.info("Mod DEMO Activ", { description: "Fișierele vor fi salvate doar local în browser." });
    }

    setIsUploading(true);
    setUploadStatus('Pregătire LexScan...');

    try {
      setUploadStatus('Încărcare în Cloud...');
      const storagePath = await db.uploadFile(file);
      
      // Incrementăm utilizarea la upload reușit
      RateLimitService.incrementUsage('uploads');

      setUploadStatus('Salvare înregistrări...');
      const newDocRecord = await db.addDocumentRecord({
        name: file.name,
        type: file.type,
        size: (file.size / 1024).toFixed(2) + ' KB',
        linkedCaseId: selectedCaseId || undefined,
        summary: 'Analiză AI în curs...'
      }, storagePath);

      setLocalDocs(prev => [newDocRecord, ...prev]);
      setActiveAnalysisId(newDocRecord.id);
      
      let preExtractedText = "";
      if (file.type === 'application/pdf') {
        setUploadStatus('Extragere text PDF...');
        preExtractedText = await extractTextFromPDF(file);
      }

      // Verificăm dacă mai are credite AI înainte de a consuma
      if (RateLimitService.checkDailyLimit('aiCalls')) {
        setUploadStatus('AI analizează documentul...');
        const reader = new FileReader();
        reader.onload = async (event) => {
          const base64 = event.target?.result as string;
          try {
            RateLimitService.incrementUsage('aiCalls');
            const analysis = await analyzeDocumentWithDeadlines(file.name, base64, file.type, preExtractedText);
            
            const finalExtractedText = analysis.extractedText || preExtractedText;
            const finalSummary = analysis.summary || "Fișier parsat, fără rezumat AI.";
            
            // Default entities structure in case it's missing from the AI response
            const defaultEntities = { persons: [], institutions: [], laws: [], dates: [] };
            const finalEntities = analysis.entities || defaultEntities;
            
            const updatedDoc = await db.updateDocumentSummary(newDocRecord.id, finalSummary, finalExtractedText, finalEntities);
            
            // --- INCEPUT LOGICA RAG (Vectorizare) ---
            if (finalExtractedText && finalExtractedText.trim().length > 0) {
              try {
                setUploadStatus('Indexare semantică (RAG)...');
                const chunks = chunkText(finalExtractedText, 500, 100); // 500 cuvinte cu 100 overlap
                for (let i = 0; i < chunks.length; i++) {
                  const chunk = chunks[i];
                  if (chunk.trim().length < 10) continue; // Skip empty chunks
                  const embedding = await generateEmbeddings(chunk);
                  if (embedding && embedding.length > 0) {
                    await db.saveDocumentChunk(newDocRecord.id, newDocRecord.linkedCaseId, chunk, embedding, { chunkIndex: i, totalChunks: chunks.length });
                  }
                }
              } catch (ragErr) {
                console.error("Eroare la indexarea semantica RAG:", ragErr);
                // Nu blocam flow-ul daca pica vectorizarea, doar o logam
              }
            }
            // --- SFARSIT LOGICA RAG ---
            
            if (updatedDoc) {
              setLocalDocs(prev => prev.map(d => d.id === updatedDoc.id ? updatedDoc : d));
              toast.success("Document analizat și indexat cu succes!", { description: "Acum este disponibil pentru asistență RAG." });
            }
          } catch (err) {
            console.error("Eroare AI:", err);
            const failMsg = preExtractedText ? "Document salvat și indexat (doar text). Eroare rezumat AI." : "Document salvat. Analiza AI a eșuat.";
            const updatedDoc = await db.updateDocumentSummary(newDocRecord.id, failMsg, preExtractedText);
            setLocalDocs(prev => prev.map(d => d.id === newDocRecord.id ? { ...d, summary: failMsg, extractedText: preExtractedText } : d));
            toast.warning("Analiză AI parțială", { description: "Textul a fost extras, dar rezumatul inteligent nu a putut fi generat." });
          } finally {
            setActiveAnalysisId(null);
            setUploadStatus('');
          }
        };
        reader.readAsDataURL(file);
      } else {
        setUploadStatus('');
        setActiveAnalysisId(null);
        toast.warning("Limită AI atinsă", { description: "Documentul a fost încărcat, dar nu a putut fi analizat de AI azi." });
      }

    } catch (err: any) {
      toast.error("Eroare la încărcare", { description: err.message || 'Verifică configurarea bucket-ului.' });
    } finally {
      setIsUploading(false);
      setUploadStatus('');
      if (e.target) e.target.value = '';
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Ștergi acest document definitiv din cloud?")) return;
    const doc = localDocs.find(d => d.id === id);
    await db.deleteDocument(id, doc?.filePath);
    setLocalDocs(prev => prev.filter(d => d.id !== id));
    toast.success("Document șters.");
  };

  const handleOpenFile = async (id: string, filePath?: string) => {
    if (!filePath) return;
    setOpeningId(id);
    try {
      const url = await db.getFileUrl(filePath);
      if (url) {
        window.open(url, '_blank');
      } else {
        toast.error("Document indisponibil", { 
          description: "Fiind în mod DEMO/Local, este posibil ca fișierul temporar să fi expirat. Te rugăm să îl încarci din nou." 
        });
      }
    } finally {
      setOpeningId(null);
    }
  };

  const renderHighlightedSummary = (summary: string, entities?: LegalDocument['entities']) => {
    if (!entities) return summary;

    // Aplatizăm și sortăm entitățile descrescător după lungime pentru a evita match-urile parțiale
    const allEntities: { word: string, type: string }[] = [];
    
    // Verificăm dacă sunt cu adevărat array-uri pentru a preveni crash-uri frontend în cazul în care AI-ul returnează string-uri
    if (Array.isArray(entities.persons)) entities.persons.forEach(p => p && typeof p === 'string' && p.trim() && allEntities.push({ word: p.trim(), type: 'person' }));
    if (Array.isArray(entities.institutions)) entities.institutions.forEach(i => i && typeof i === 'string' && i.trim() && allEntities.push({ word: i.trim(), type: 'institution' }));
    if (Array.isArray(entities.laws)) entities.laws.forEach(l => l && typeof l === 'string' && l.trim() && allEntities.push({ word: l.trim(), type: 'law' }));
    if (Array.isArray(entities.dates)) entities.dates.forEach(d => d && typeof d === 'string' && d.trim() && allEntities.push({ word: d.trim(), type: 'date' }));

    if (allEntities.length === 0) return summary;

    // Eliminăm duplicatele (case-insensitive fallback) pentru regex și sortăm
    const uniqueWords = Array.from(new Set(allEntities.map(e => e.word)))
      .filter(w => w.length > 2) // ignorăm cuvinte prea scurte pentru a evita highlight-uri false (ex: "la", "de")
      .sort((a, b) => b.length - a.length);
      
    if (uniqueWords.length === 0) return summary;

    // Escape regex
    const escapeRegExp = (string: string) => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    
    // Adăugăm flag-ul 'gi' (global + case insensitive)
    const regexPattern = new RegExp(`(${uniqueWords.map(escapeRegExp).join('|')})`, 'gi');

    const parts = summary.split(regexPattern);

    return parts.map((part, index) => {
      // Căutăm match-ul ignorând case-ul (fiindcă regex-ul 'i' a extras cuvântul cu case-ul original din summary, nu din listă)
      const entityMatch = allEntities.find(e => e.word.toLowerCase() === part.toLowerCase());
      if (entityMatch) {
        let bgColor = 'bg-slate-200 text-slate-800';
        if (entityMatch.type === 'person') bgColor = 'bg-blue-100 text-blue-800 border-blue-200';
        if (entityMatch.type === 'institution') bgColor = 'bg-amber-100 text-amber-800 border-amber-200';
        if (entityMatch.type === 'law') bgColor = 'bg-emerald-100 text-emerald-800 border-emerald-200';
        if (entityMatch.type === 'date') bgColor = 'bg-rose-100 text-rose-800 border-rose-200';

        return (
          <span key={index} className={`px-1.5 py-0.5 rounded-md font-bold text-xs not-italic border shadow-sm mx-0.5 inline-block ${bgColor}`}>
            {part}
          </span>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-7xl mx-auto space-y-8 animate-in fade-in duration-700 pb-32">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-8 bg-white p-6 md:p-10 rounded-[2.5rem] border border-slate-100 shadow-sm">
        <div className="space-y-1">
          <h2 className="text-2xl md:text-4xl font-black text-slate-900 tracking-tight italic">LexScan Archive</h2>
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${isSupabaseConfigured ? 'bg-emerald-500' : 'bg-amber-500 animate-pulse'}`}></span>
            <p className="text-slate-400 font-bold uppercase text-[9px] tracking-widest italic">
              {isSupabaseConfigured ? 'Cloud Storage Privat' : 'Mod Demo - Salvare Locală'}
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-4 w-full lg:w-auto">
          <button 
            onClick={() => setIsCalculatorOpen(true)}
            className="w-full sm:w-auto h-[64px] flex items-center justify-center gap-3 px-8 bg-slate-900 hover:bg-slate-800 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl shadow-slate-200 transition-all active:scale-95 shrink-0"
          >
            <Calculator className="w-5 h-5" />
            Calculator Termene
          </button>

          <div className="relative w-full sm:w-64">
            <select 
              aria-label="Selectează Dosar"
              className="w-full h-[64px] pl-6 pr-12 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-black text-[10px] uppercase tracking-widest text-slate-600 appearance-none focus:border-indigo-500 transition-all italic shadow-inner"
              value={selectedCaseId}
              onChange={e => setSelectedCaseId(e.target.value)}
            >
              <option value="">Fără Dosar Specific</option>
              {cases.map(c => <option key={c.id} value={c.id}>{c.caseNumber}</option>)}
            </select>
            <div className="absolute right-6 top-1/2 -translate-y-1/2 pointer-events-none text-indigo-500 font-black">▼</div>
          </div>

          <label className={`w-full sm:w-auto h-[64px] flex items-center justify-center gap-3 px-10 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl shadow-indigo-100 transition-all active:scale-95 cursor-pointer shrink-0 ${isUploading ? 'opacity-50' : ''}`}>
            <span className="text-xl">{isUploading ? '⌛' : '📂'}</span>
            {isUploading ? uploadStatus : 'ÎNCARCĂ DOCUMENT'}
            <input type="file" className="hidden" onChange={handleFileUpload} accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.txt" disabled={isUploading} />
          </label>
        </div>
      </div>

      <div className="grid gap-6 md:gap-10 w-full">
        {localDocs.length === 0 ? (
          <div className="py-24 text-center bg-white rounded-[3rem] border-2 border-dashed border-slate-100 w-full">
            <p className="text-2xl font-black text-slate-200 italic tracking-tight">Arhivă Goală</p>
          </div>
        ) : localDocs.map(doc => (
          <div key={doc.id} className="bg-white rounded-[2.5rem] md:rounded-[4rem] border border-slate-50 shadow-2xl overflow-hidden group hover:border-indigo-200 transition-all animate-in slide-in-from-top duration-500">
            <div className="p-6 md:p-12 flex flex-col lg:flex-row gap-8">
              <div className="lg:w-1/4 shrink-0">
                <div className="w-16 h-16 bg-slate-50 rounded-[1.5rem] flex items-center justify-center text-3xl mb-6 shadow-xl group-hover:scale-110 transition-transform">📄</div>
                <h4 className="font-black text-slate-900 text-lg leading-tight mb-3 break-words italic">{doc.name}</h4>
                <p className="text-[8px] font-black text-slate-400 uppercase tracking-wider mb-6 italic">{doc.type} • {doc.size}</p>
                
                <div className="flex flex-col gap-3">
                   <button 
                     onClick={() => handleOpenFile(doc.id, doc.filePath)} 
                     disabled={openingId === doc.id}
                     className="bg-slate-900 text-white py-4 rounded-2xl text-[9px] font-black uppercase tracking-widest hover:bg-indigo-600 transition-all shadow-lg flex items-center justify-center gap-2"
                   >
                     {openingId === doc.id ? '⌛ Deschidere...' : '👁️ Vezi Document'}
                   </button>
                   <button onClick={() => handleDelete(doc.id)} className="text-[8px] font-black text-rose-500 uppercase tracking-widest hover:underline italic text-left ml-2">Elimină</button>
                </div>
              </div>
              <div className="flex-grow">
                <div className="bg-slate-50 p-6 md:p-10 rounded-[2rem] md:rounded-[3.5rem] border border-slate-50 relative overflow-hidden min-h-[160px] flex flex-col xl:flex-row gap-8">
                  {/* Rezumat Text */}
                  <div className="flex-1 space-y-6">
                   <div className="flex items-center justify-between mb-6">
                     <span className="text-[9px] font-black text-indigo-600 bg-white px-5 py-2.5 rounded-xl uppercase tracking-widest italic border border-indigo-50 shadow-sm">✨ Rezumat LexScan Pro</span>
                     {doc.entities && (doc.entities.persons?.length > 0 || doc.entities.dates?.length > 0) && (
                       <div className="flex gap-2">
                         {doc.entities.persons?.length > 0 && <span className="text-[8px] font-black uppercase text-blue-500 bg-blue-50 px-2 py-1 rounded-md">👤 Nume Identificate</span>}
                         {doc.entities.dates?.length > 0 && <span className="text-[8px] font-black uppercase text-rose-500 bg-rose-50 px-2 py-1 rounded-md">📅 Termene Găsite</span>}
                       </div>
                     )}
                   </div>
                   <p className="text-sm md:text-lg text-slate-700 italic font-medium leading-relaxed whitespace-pre-wrap">
                     {activeAnalysisId === doc.id ? "Analiză AI în curs..." : renderHighlightedSummary(doc.summary || "Fără rezumat.", doc.entities)}
                   </p>
                  </div>

                  {/* Dashboard Analiză AI (Partea dreaptă) */}
                  {doc.analysis && (doc.analysis.actionPlan?.length > 0 || doc.analysis.legalConflicts?.length > 0) && (
                    <div className="w-full xl:w-80 shrink-0 flex flex-col gap-4 border-t xl:border-t-0 xl:border-l border-slate-200/60 pt-6 xl:pt-0 xl:pl-8">
                      {doc.deadlines && doc.deadlines.length > 0 && (
                        <div className="bg-white p-5 rounded-[1.5rem] shadow-sm border border-slate-100">
                          <h5 className="text-[10px] font-black uppercase tracking-widest text-indigo-600 mb-3 flex items-center gap-2">
                            <CalendarIcon className="w-4 h-4" /> Termene Extrase
                          </h5>
                          <ul className="space-y-4">
                            {doc.deadlines.map((deadline, idx) => (
                              <li key={idx} className="flex flex-col gap-2">
                                <div className="flex items-start gap-2">
                                  <span className="text-indigo-400 mt-0.5">•</span>
                                  <div>
                                    <p className="text-[11px] font-bold text-slate-800 leading-snug">{deadline.date}</p>
                                    <p className="text-[11px] font-medium text-slate-500 leading-snug">{deadline.description}</p>
                                  </div>
                                </div>
                                <button
                                  onClick={() => onAddEvent({
                                    id: Math.random().toString(),
                                    title: `Termen Extras: ${doc.name}`,
                                    date: deadline.date,
                                    description: deadline.description,
                                    type: 'deadline',
                                    linkedCaseId: doc.linkedCaseId
                                  })}
                                  className="self-start ml-4 px-3 py-1.5 bg-slate-50 hover:bg-indigo-50 text-slate-600 hover:text-indigo-600 rounded-lg text-[9px] font-bold uppercase tracking-wider transition-colors flex items-center gap-1 border border-slate-200"
                                >
                                  <CalendarIcon className="w-3 h-3" />
                                  Adaugă în Calendar
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {doc.analysis.actionPlan && doc.analysis.actionPlan.length > 0 && (
                        <div className="bg-white p-5 rounded-[1.5rem] shadow-sm border border-slate-100">
                          <h5 className="text-[10px] font-black uppercase tracking-widest text-emerald-600 mb-3 flex items-center gap-2">
                            <span>✅</span> Plan de Acțiune
                          </h5>
                          <ul className="space-y-2">
                            {doc.analysis.actionPlan.map((step, idx) => (
                              <li key={idx} className="text-[11px] font-medium text-slate-600 leading-snug flex items-start gap-2">
                                <span className="text-emerald-400 mt-0.5">•</span>
                                {step}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {doc.analysis.legalConflicts && doc.analysis.legalConflicts.length > 0 && (
                        <div className="bg-white p-5 rounded-[1.5rem] shadow-sm border border-slate-100">
                          <h5 className="text-[10px] font-black uppercase tracking-widest text-rose-600 mb-3 flex items-center gap-2">
                            <span>⚖️</span> Conflicte / Excepții
                          </h5>
                          <ul className="space-y-2">
                            {doc.analysis.legalConflicts.map((conflict, idx) => (
                              <li key={idx} className="text-[11px] font-bold text-slate-700 leading-snug flex items-start gap-2">
                                <span className="text-rose-400 mt-0.5">⚠️</span>
                                {conflict}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <button
                        onClick={() => {
                          const contextStr = `Părți implicate: ${doc.entities?.persons?.join(', ') || '-'}\n` +
                                             `Instanțe: ${doc.entities?.institutions?.join(', ') || '-'}\n` +
                                             `Rezumat document: ${doc.summary}\n` +
                                             `Plan de acțiune: ${doc.analysis?.actionPlan?.join('; ') || '-'}`;
                          navigate('/editor', { state: { prefillContext: contextStr } });
                        }}
                        className="w-full py-3 mt-2 bg-indigo-50 text-indigo-700 hover:bg-indigo-600 hover:text-white rounded-xl font-black text-[10px] uppercase tracking-widest transition-all shadow-sm flex items-center justify-center gap-2"
                      >
                        <Wand2 className="w-4 h-4" />
                        Crează Draft din Analiză
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {isCalculatorOpen && (
        <DeadlineCalculatorModal 
          onClose={() => setIsCalculatorOpen(false)}
          onSaveToCalendar={onAddEvent}
          cases={cases}
        />
      )}
    </div>
  );
};

export default DocumentsView;
